import React from 'react';
import { useIonRouter, IonIcon } from '@ionic/react';
import { lockClosed } from 'ionicons/icons';
import { Course } from '@/types/course';

interface CourseCardProps {
  course: Course;
}

export const CourseCard: React.FC<CourseCardProps> = ({ course }) => {
  const router = useIonRouter();

  const handleCardClick = () => {
    if (course.disabled) {
      return;
    }
    // Pass certificationId as query parameter to skip certification selection (only if defined)
    const url = course.certificationId
      ? `/course/${course.slug}?certificationId=${course.certificationId}`
      : `/course/${course.slug}`;
    router.push(url, 'forward');
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col hover:shadow-md transition-shadow h-[350px] ${!course.disabled ? 'cursor-pointer' : ''} relative`}
      onClick={handleCardClick}
    >
      {/* Disabled Overlay */}
      {course.disabled && (
        <div className="absolute inset-0 bg-white/70 z-20 rounded-lg">
          {/* Diagonal "crossed out" bar */}
          <div
            className="absolute bg-red-500/30 w-[150%] h-6 flex items-center justify-center"
            style={{
              transform: 'rotate(-45deg)',
              transformOrigin: 'center',
              top: '50%',
              left: '-25%',
            }}
          >
            <IonIcon icon={lockClosed} className="disabled-lock" style={{ transform: 'rotate(45deg)' }} />
          </div>
        </div>
      )}

      {/* Image Section */}
      <div className="relative h-[180px] bg-[#647386]">
        {course.imageUrl && (
          <img
            src={course.imageUrl}
            alt={course.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback if image doesn't load
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        <div className="absolute top-4 left-4">
          <span className={`${course.progress >= 100 ? 'bg-green-500' : course.progress > 0 ? 'bg-tertiary' : 'bg-red-500'} text-white px-3 py-1 rounded text-sm font-semibold uppercase`}>
            {course.progress >= 100 ? 'Completed' : course.progress > 0 ? 'Resume Course' : 'Start Course'}
          </span>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-6">
        <div className="h-[56px] mb-8">
          <p className="text-gray-700 text-sm mb-1">
            {course.steps || 0} {course.steps > 1 ? 'Steps' : 'Step'}
          </p>
          <h3 className="text-xl font-bold line-clamp-2 overflow-hidden">{course.name}</h3>
        </div>

        {/* Progress Bar */}
        <div>
          <div className="w-full h-2 bg-medium rounded-full overflow-hidden">
            <div
              className="h-full bg-success rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${Math.min(Math.max(course.progress, 0), 100)}%` }}
            />
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-gray-600 text-sm">{course.progress || 0}% Complete</span>
            <span className="text-gray-600 text-sm">{course.lessonsCompleted || 0}/{course.steps || 0} Steps</span>
          </div>
        </div>
      </div>
    </div>
  );
};
